import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "./slider.css";

// Lazy load images using dynamic imports or optimized imports
const imageModules = {
  image2: () => import("./sliderimage/imageslider.png"),
  image4: () => import("./sliderimage/WhatsApp Image 2025-07-30 at 09.40.20 (1).webp"),
  image5: () => import("../images/IMG_1550 (FILEminimizer).JPG"),
  image7: () => import("./sliderimage/WhatsApp Image 2025-05-24 at 10.50.44.webp"),
  image6: () => import("../images/IMG_1634 (FILEminimizer).JPG"),
};

const Slider = () => {
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedImages, setLoadedImages] = useState({});
  
  const imageRefs = useRef([]);
  const preloadTimerRef = useRef(null);

  // Image data with captions (separate from actual image sources)
  const imageData = useMemo(() => [
    { id: 'image2', alt: "College Campus View", caption: "Excellence in Technical Education" },
    { id: 'image4', alt: "Students in Lab", caption: "State-of-the-art Laboratories" },
    { id: 'image5', alt: "College Building", caption: "Modern Infrastructure" },
    // { id: 'image6', alt: "Sports Facility", caption: "Holistic Development" },
    { id: 'image7', alt: "Graduation Day", caption: "Building Future Leaders" },
  ], []);

  // Load images dynamically
  useEffect(() => {
    const loadImages = async () => {
      try {
        setIsLoading(true);
        const loadedImageModules = await Promise.all(
          Object.values(imageModules).map(importFn => importFn())
        );
        
        const imagesList = imageData.map((data, index) => ({
          ...data,
          src: loadedImageModules[index].default || loadedImageModules[index]
        }));
        
        setImages(imagesList);
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to load images:", error);
        setIsLoading(false);
      }
    };

    loadImages();
  }, [imageData]);

  // Optimized navigation functions with useCallback
  const prevImage = useCallback(() => {
    if (images.length === 0) return;
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
    resetAutoPlay();
  }, [images.length]);

  const nextImage = useCallback(() => {
    if (images.length === 0) return;
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
    resetAutoPlay();
  }, [images.length]);

  const goToSlide = useCallback((index) => {
    if (index === currentIndex) return;
    setCurrentIndex(index);
    resetAutoPlay();
  }, [currentIndex]);

  const resetAutoPlay = useCallback(() => {
    setIsAutoPlaying(false);
    // Clear existing timeout
    if (preloadTimerRef.current) {
      clearTimeout(preloadTimerRef.current);
    }
    // Restart autoplay after 5 seconds
    preloadTimerRef.current = setTimeout(() => setIsAutoPlaying(true), 5000);
  }, []);

  // Preload next and previous images
  useEffect(() => {
    if (images.length === 0) return;

    const preloadImages = () => {
      const nextIndex = (currentIndex + 1) % images.length;
      const prevIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
      
      [nextIndex, prevIndex].forEach(index => {
        if (!loadedImages[images[index].id] && images[index]?.src) {
          const img = new Image();
          img.src = images[index].src;
          img.onload = () => {
            setLoadedImages(prev => ({ ...prev, [images[index].id]: true }));
          };
        }
      });
    };

    preloadImages();
  }, [currentIndex, images, loadedImages]);

  // Autoplay functionality
  useEffect(() => {
    let intervalId;
    if (isAutoPlaying && images.length > 0) {
      intervalId = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
      }, 5000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isAutoPlaying, images.length]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (preloadTimerRef.current) {
        clearTimeout(preloadTimerRef.current);
      }
    };
  }, []);

  // Handle image load completion
  const handleImageLoad = useCallback((id) => {
    setLoadedImages(prev => ({ ...prev, [id]: true }));
  }, []);

  if (isLoading || images.length === 0) {
    return (
      <div className="slider-container">
        <div className="slider-loader">
          <div className="spinner"></div>
          <p>Loading images...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="slider-container">
      <div className="slider">
        <div className="image-container">
          {/* Low-quality placeholder or loader */}
          {!loadedImages[images[currentIndex].id] && (
            <div className="image-loader">
              <div className="spinner-small"></div>
            </div>
          )}
          
          <img
            ref={el => imageRefs.current[currentIndex] = el}
            src={images[currentIndex].src}
            alt={images[currentIndex].alt}
            className={`slider-image ${loadedImages[images[currentIndex].id] ? 'loaded' : 'loading'}`}
            loading="lazy"
            onLoad={() => handleImageLoad(images[currentIndex].id)}
          />
          
          <div className="slide-caption">
            <h2>{images[currentIndex].caption}</h2>
          </div>
        </div>
        
        <button 
          onClick={prevImage} 
          className="slider-btn prev-btn" 
          aria-label="Previous slide"
          disabled={isLoading}
        >
          <span>&#10094;</span>
        </button>
        
        <button 
          onClick={nextImage} 
          className="slider-btn next-btn" 
          aria-label="Next slide"
          disabled={isLoading}
        >
          <span>&#10095;</span>
        </button>
        
        <div className="slide-indicators">
          {images.map((_, index) => (
            <button
              key={index}
              className={`indicator ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
              disabled={isLoading}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Slider;